import React, { useEffect, useState } from "react";
import "./listAllUsers.css";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import { useMutation } from "@apollo/client";
import { UPDATE_USER_LEGAL_NAME } from "../../../../utils/GraphQL/mutations";

// Legal name editor (2026-08-18). Paystub PDFs print the legal name when set;
// blank = stubs keep using the display name. BOTH-OR-NEITHER is enforced here
// AND server-side: set both fields, or clear both — a half-set pair would let
// an official document print a name that belongs to nobody.
const LegalNameModal = ({
  selectedUser,
  closeModal,
  handleSnack,
  updateUserLegalNameCB,
}) => {
  const [legalFirst, setLegalFirst] = useState("");
  const [legalLast, setLegalLast] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [updateLegalName] = useMutation(UPDATE_USER_LEGAL_NAME);

  useEffect(() => {
    // Prefill legal if set, else display — the common "mimic the account"
    // case costs one glance + Update.
    setLegalFirst(selectedUser?.legalFirstName || selectedUser?.firstName || "");
    setLegalLast(selectedUser?.legalLastName || selectedUser?.lastName || "");
  }, [selectedUser]);

  const handleUpdate = async () => {
    const first = legalFirst.trim();
    const last = legalLast.trim();

    // Both-or-neither. Mark the EMPTY one, since that is the field to act on.
    if ((first === "") !== (last === "")) {
      const message =
        "Set both legal names, or clear both to fall back to the display name.";
      setFieldErrors(first === "" ? { first: message } : { last: message });
      return;
    }
    setFieldErrors({});

    setSaving(true);
    try {
      const { data } = await updateLegalName({
        variables: {
          userId: selectedUser?._id,
          legalFirstName: first,
          legalLastName: last,
        },
      });

      if (data?.updateUserLegalName) {
        handleSnack(
          first
            ? `Legal name saved — paystubs will print ${first} ${last}`
            : "Legal name cleared — paystubs will use the display name",
          4000,
        );
        updateUserLegalNameCB({
          userId: selectedUser?._id,
          legalFirstName: first,
          legalLastName: last,
        });
        closeModal();
        return;
      }

      handleSnack("Something went wrong...", 3000);
    } catch (err) {
      handleSnack(err?.message || "Failed to save legal name", 4000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BdrModal
      open
      onClose={closeModal}
      title={`Legal Name — ${selectedUser?.firstName} ${selectedUser?.lastName}`}
      variant="panel"
      size="md"
      actions={
        <>
          <BdrButton onClick={closeModal}>Cancel</BdrButton>
          {/* solid: this modal sits on the saturated --bdr-grad-panel */}
          <BdrButton
            variant="confirm-solid"
            disabled={saving}
            onClick={handleUpdate}
          >
            {saving ? "Saving..." : "Update"}
          </BdrButton>
        </>
      }
    >
      <div className="lau-field-stack">
        <BdrInput
          type="text"
          label="Legal First Name"
          name="legalFirstName"
          value={legalFirst}
          error={fieldErrors.first}
          onChange={(e) => {
            setLegalFirst(e.target.value);
            setFieldErrors({});
          }}
          helper="Printed on paystub PDFs only. Clear both fields to go back to the display name."
        />
        <BdrInput
          type="text"
          label="Legal Last Name"
          name="legalLastName"
          value={legalLast}
          error={fieldErrors.last}
          onChange={(e) => {
            setLegalLast(e.target.value);
            setFieldErrors({});
          }}
        />
      </div>
    </BdrModal>
  );
};

export default LegalNameModal;
